<template>
  <div class="ocr-extract">
    <div class="ocr-extract-header">
      <div class="header-left">
        <router-link to="/integrations/page/protokol-media/ocr-models" class="back-link">
          <i class="fa-solid fa-arrow-left"></i>
        </router-link>
        <h2 class="header-title">{{ $t('integrations.media.ocr.extractTitle') }}</h2>
      </div>
    </div>

    <div class="ocr-extract-body">
      <div class="extract-form">
        <div class="form-section">
          <label class="section-label">{{ $t('integrations.media.ocr.title') }}</label>
          <select v-model="selectedModelUuid" class="form-input" @change="onModelChange">
            <option value="" disabled>{{ $t('integrations.media.ocr.selectModel') }}</option>
            <option v-for="m in models" :key="m.uuid" :value="m.uuid">
              {{ m.name }} ({{ m.engine === 'kortex' ? $t('integrations.media.ocr.engineKortex') : $t('integrations.media.ocr.engineDefault') }})
            </option>
          </select>
        </div>

        <div v-if="selectedModelUsesKortex && !isKortexAvailable" class="engine-gate">
          <i class="fa-solid fa-lock"></i>
          <div class="engine-gate-text">
            <span class="engine-gate-title">{{ $t('integrations.media.ocr.kortexExtractGateTitle') }}</span>
            <span class="engine-gate-desc">{{ $t('integrations.media.ocr.kortexExtractGateDesc') }}</span>
          </div>
          <router-link
            :to="{ name: 'marketplace.List', query: { open: 'protokol-kortex' } }"
            class="engine-gate-btn"
          >
            <i class="fa-solid fa-store"></i> {{ $t('integrations.media.ocr.kortexGateBtn') }}
          </router-link>
        </div>

        <div class="form-section">
          <label class="section-label">{{ $t('integrations.media.ocr.image') }}</label>
          <div class="image-upload-area">
            <div v-if="imagePreview" class="image-preview-wrap">
              <img :src="imagePreview" class="image-preview" />
              <button class="remove-image" @click="clearImage">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <label v-else class="upload-zone">
              <input type="file" accept="image/*" @change="onImageSelect" hidden />
              <i class="fa-solid fa-cloud-arrow-up"></i>
              <span>{{ $t('integrations.media.ocr.uploadImage') }}</span>
            </label>
          </div>
        </div>

        <div v-if="imagePreview" class="form-section">
          <label class="section-label">
            {{ $t('integrations.media.ocr.documentBoundary') }}
            <span class="hint">({{ $t('integrations.media.ocr.documentBoundaryHint') }})</span>
          </label>
          <div class="boundary-cropper-wrap">
            <img
              ref="extractImage"
              :src="imagePreview"
              class="extract-img"
              @load="onExtractImageLoad"
            />
          </div>
        </div>

        <button
          class="btn-extract"
          @click="runExtraction"
          :disabled="!selectedModelUuid || !imageFile || extracting || (selectedModelUsesKortex && !isKortexAvailable)"
        >
          <i :class="extracting ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-magnifying-glass'"></i>
          {{ extracting ? $t('integrations.media.ocr.extracting') : $t('integrations.media.ocr.extractBtn') }}
        </button>
      </div>

      <!-- Results -->
      <div class="extract-results" v-if="result">
        <div class="results-header">
          <h3>{{ $t('integrations.media.ocr.results') }} — {{ result.model_name }}</h3>
          <button class="btn-copy" @click="copyJson">
            <i class="fa-solid fa-copy"></i>
            {{ $t('integrations.media.ocr.copyJson') }}
          </button>
        </div>
        <table class="results-table">
          <thead>
            <tr>
              <th>{{ $t('integrations.media.ocr.label') }}</th>
              <th>{{ $t('integrations.media.ocr.extractedText') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(text, label) in result.results" :key="label">
              <td class="label-cell">{{ label }}</td>
              <td class="text-cell">{{ text }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="result.errors && result.errors.length" class="results-errors">
          <h4>{{ $t('integrations.media.ocr.errors') }}</h4>
          <ul>
            <li v-for="(err, i) in result.errors" :key="i">{{ err }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.min.css'

export default {
  props: {
    context: { type: Object },
  },

  data() {
    return {
      selectedModelUuid: '',
      imageFile: null,
      imagePreview: null,
      documentBoundary: null,
      extracting: false,
      result: null,
      cropper: null,
    }
  },

  computed: {
    models() {
      return this.$store.getters['ocrModels/models']
    },
    selectedModel() {
      return this.models.find(m => m.uuid === this.selectedModelUuid)
    },
    selectedModelUsesKortex() {
      return this.selectedModel && this.selectedModel.engine === 'kortex'
    },
    isKortexAvailable() {
      return this.$store.getters['integrations/isIntegrationActive']('protokol-kortex')
    },
  },

  async mounted() {
    const plan = await this.$store.dispatch('ocrModels/fetchPlan')
    if (!plan || plan.plan !== 'paid') {
      this.$router.replace('/integrations/page/protokol-media/page')
      return
    }
    this.$store.dispatch('ocrModels/fetchModels')
    const uuid = this.$route.query.uuid
    if (uuid) this.selectedModelUuid = uuid
  },

  beforeDestroy() {
    this.destroyCropper()
  },

  methods: {
    onModelChange() {
      this.result = null
    },

    onImageSelect(e) {
      const file = e.target.files[0]
      if (!file) return
      this.imageFile = file
      this.imagePreview = URL.createObjectURL(file)
      this.documentBoundary = null
      this.result = null
      e.target.value = ''
    },

    clearImage() {
      this.destroyCropper()
      this.imageFile = null
      this.imagePreview = null
      this.documentBoundary = null
      this.result = null
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    onExtractImageLoad() {
      this.$nextTick(() => this.initCropper())
    },

    initCropper() {
      this.destroyCropper()
      const img = this.$refs.extractImage
      if (!img) return

      this.cropper = new Cropper(img, {
        viewMode: 1,
        dragMode: 'crop',
        autoCrop: false,
        guides: true,
        center: true,
        highlight: true,
        background: false,
        responsive: true,
        cropend: () => {
          this.saveBoundaryFromCropper()
        },
      })
    },

    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
    },

    saveBoundaryFromCropper() {
      if (!this.cropper) return
      const cropData = this.cropper.getData(true)
      const imageData = this.cropper.getImageData()

      this.documentBoundary = {
        x: cropData.x / imageData.naturalWidth,
        y: cropData.y / imageData.naturalHeight,
        width: cropData.width / imageData.naturalWidth,
        height: cropData.height / imageData.naturalHeight,
      }
    },

    async runExtraction() {
      if (!this.imageFile || !this.selectedModelUuid) return

      this.extracting = true
      this.result = null

      try {
        const base64 = await this.fileToBase64(this.imageFile)

        const payload = {
          model_uuid: this.selectedModelUuid,
          image: base64,
        }
        if (this.documentBoundary) {
          payload.document_boundary = this.documentBoundary
        }

        this.result = await this.$store.dispatch('ocrModels/extract', payload)
      } catch (e) {
        console.error('Extraction failed', e)
        this.result = { model_name: 'Error', results: {}, errors: [e.message || 'Extraction failed'] }
      } finally {
        this.extracting = false
      }
    },

    copyJson() {
      if (!this.result) return
      navigator.clipboard.writeText(JSON.stringify(this.result.results, null, 2))
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_custom.scss";

.ocr-extract {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.ocr-extract-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-default);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: var(--text-muted);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  text-decoration: none;

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-hover);
  }
}

.header-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.ocr-extract-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  max-width: 900px;
}

.extract-form {
  margin-bottom: 32px;
}

.form-section {
  margin-bottom: 20px;
}

.section-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  .hint {
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.75rem;
  }
}

.engine-gate {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  padding: 14px 16px;
  border-radius: 10px;
  background: rgba(234, 179, 8, 0.08);
  border: 1px solid rgba(234, 179, 8, 0.25);

  > i {
    color: #eab308;
    font-size: 1rem;
  }
}

.engine-gate-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.engine-gate-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
}

.engine-gate-desc {
  font-size: 0.78rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.engine-gate-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eab308, #f59e0b);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
    color: #fff;
    text-decoration: none;
  }
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--bg-main);
  color: var(--text-primary);
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: #10b981;
  }
}

.image-upload-area {
  margin-top: 4px;
}

.image-preview-wrap {
  position: relative;
  display: inline-block;
}

.image-preview {
  max-width: 400px;
  max-height: 300px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
}

.remove-image {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px;
  border: 2px dashed var(--border-default);
  border-radius: 12px;
  color: var(--text-muted);
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: #10b981;
    color: #10b981;
  }

  i { font-size: 1.5rem; }
  span { font-size: 0.9rem; }
}

.boundary-cropper-wrap {
  display: inline-block;
  max-width: 100%;
}

.extract-img {
  display: block;
  max-width: 100%;
  max-height: 500px;
}

::v-deep .cropper-view-box,
::v-deep .cropper-face {
  border-color: #22c55e;
  outline-color: #22c55e;
}

::v-deep .cropper-dashed {
  border-color: #22c55e;
}

::v-deep .cropper-point {
  background-color: #22c55e;
}

::v-deep .cropper-line {
  background-color: #22c55e;
}

.btn-extract {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 24px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Results */
.extract-results {
  margin-top: 8px;
}

.results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
  }
}

.btn-copy {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-hover);
  }
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid var(--border-default);
  border-radius: 10px;
  overflow: hidden;

  th, td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid var(--border-default);
  }

  th {
    background: var(--bg-main);
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .label-cell {
    font-weight: 600;
    color: var(--text-primary);
    width: 200px;
  }

  .text-cell {
    color: var(--text-primary);
  }
}

.results-errors {
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;

  h4 {
    color: #ef4444;
    margin: 0 0 8px;
    font-size: 0.9rem;
  }

  ul {
    margin: 0;
    padding: 0 0 0 20px;
    color: #ef4444;
    font-size: 0.85rem;
  }
}
</style>
